// src/routes.tsx
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MainPage from './pages/MainPage';
import ApplicationPage from './pages/ApplicationPage';
import FirebaseTestPage from './pages/FirebaseTestPage';
import AuthComplete from './pages/AuthComplete';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import OrdersPage from './pages/admin/OrdersPage';
import InflowStatsPage from './pages/admin/InflowStatsPage';
import SettingsPage from './pages/admin/SettingsPage';
import PopupAdminPage from './pages/admin/PopupAdminPage';
import PrivateRoute from './routes/PrivateRoute';
import DanalTestPage from './pages/DanalTestPage';
import VerifyRedirect from './pages/VerifyRedirect';
import PrivacyPolicy from './pages/PrivacyPolicy';
import MyApplicationsPage from './pages/MyApplicationsPage';
import ApplicationDetailPage from './pages/ApplicationDetailPage';
import AdminAlertsPage from './pages/admin/AdminAlertsPage'; 
import RejectPage from './pages/RejectPage'; 
import AcceptPage from './pages/AcceptPage';
import ContractPage from './pages/ContractPage';
import CustomerInfoCompletePage from './pages/CustomerInfoCompletePage';
import AdminApplicationDetailPage from './pages/admin/AdminApplicationDetailPage';
import BackupManagementPage from './pages/admin/BackupManagementPage';
import PriceManagementPage from './pages/admin/PriceManagementPage';
import FloatingActionMenu from './pages/components/main/FloatingActionMenu';
import InflowTracker from './pages/components/InflowTracker';
import FaqManagementPage from './pages/admin/FaqManagementPage';
import NoticeManagementPage from './pages/admin/NoticeManagementPage';
import SSOTTestLabPage from './pages/admin/SSOTTestLabPage';
import TrustCertificationPage from './pages/TrustCertificationPage';
import ReviewWritePage from './pages/ReviewWritePage';
import NoticesPage from './pages/NoticesPage';
import FaqPage from './pages/FaqPage';

import { ChannelLayout } from './contexts/ChannelContext';

/**
 * 멀티 플로팅 버튼 조건부 렌더링 컴포넌트
 * - /admin 경로에서는 숨김 처리
 * - 메인 버튼 클릭 시 FAQ, 카카오톡 등 메뉴 펼쳐짐
 */
function ConditionalFloatingMenu() {
  const location = useLocation();
  
  // 관리자 페이지(/admin)에서는 플로팅 버튼 숨김
  if (location.pathname.startsWith('/admin')) {
    return null;
  }
  
  return <FloatingActionMenu />;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      {/* 마케팅 유입경로(utm/referrer) 수집 - 렌더링 없음 */}
      <InflowTracker />
      {/* 멀티 플로팅 버튼 - 사용자 페이지에서만 표시 */}
      <ConditionalFloatingMenu />
      <Routes>
        {/* ══════════════════════════════════════════════════════
            B2B(거래처) 채널 - 기존 루트 경로
            매장 코드 입력 O, 거래처 대상 메인 페이지
        ══════════════════════════════════════════════════════ */}
        <Route element={<ChannelLayout channel="b2b" />}>
          <Route path="/" element={<MainPage />} />
          <Route path="/apply" element={<ApplicationPage />} />
          <Route path="/my-list" element={<MyApplicationsPage />} />
          <Route path="/my-list/:id" element={<ApplicationDetailPage />} />
          <Route path="/notices" element={<NoticesPage />} />
          <Route path="/faq" element={<FaqPage />} />
        </Route>

        {/* ══════════════════════════════════════════════════════
            B2C(개인고객) 채널 - /dawontrade-in 경로 접두사
            매장 코드 입력 X, 개인 고객 특화 메인 페이지
        ══════════════════════════════════════════════════════ */}
        <Route path="/dawontrade-in" element={<ChannelLayout channel="b2c" />}>
          <Route index element={<MainPage />} />
          <Route path="apply" element={<ApplicationPage />} />
          <Route path="my-list" element={<MyApplicationsPage />} />
          <Route path="my-list/:id" element={<ApplicationDetailPage />} />
          <Route path="notices" element={<NoticesPage />} />
          <Route path="faq" element={<FaqPage />} />
        </Route>

        {/* ══════════════════════════════════════════════════════
            공유 페이지 (채널 무관) - 알림톡 링크 등에서 직접 접근
        ══════════════════════════════════════════════════════ */}
        <Route path="/trust" element={<TrustCertificationPage />} />
        <Route path="/review" element={<ReviewWritePage />} />
        <Route path="/auth-complete" element={<AuthComplete />} />
        <Route path="/firebase-test" element={<FirebaseTestPage />} />
        <Route path="/test/danal" element={<DanalTestPage />} />
        <Route path="/verify-redirect" element={<VerifyRedirect />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/reject/:application_id" element={<RejectPage />} />
        <Route path="/accept/:application_id" element={<AcceptPage />} />
        <Route path="/contract/:application_id" element={<ContractPage />} />
        <Route path="/customer/complete-info" element={<CustomerInfoCompletePage />} />

        {/* 관리자 로그인 */}
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* 관리자 보호 영역 */}
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <AdminLayout />
            </PrivateRoute>
          }
        >
          {/* ✅ /admin 접속 시 dashboard로 자동 리다이렉트 */}
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="popup" element={<PopupAdminPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="inflow-stats" element={<InflowStatsPage />} />
          <Route path="applications/:id" element={<AdminApplicationDetailPage />} />
          <Route path="alerts" element={<AdminAlertsPage />} />
          <Route path="backup" element={<BackupManagementPage />} />
          <Route path="price-management" element={<PriceManagementPage />} />
          <Route path="notices" element={<NoticeManagementPage />} />
          <Route path="faq" element={<FaqManagementPage />} />
          <Route path="ssot-test-lab" element={<SSOTTestLabPage />} />

        </Route>
      </Routes>
    </BrowserRouter>
  );
}
